/**
 * 비로그인 상태의 사진 — **이 브라우저 안에만** 둔다.
 *
 * 왜 이게 필요한가: 사진이 필수인데 업로드는 로그인이 필요하다. 둘을 그대로
 * 두면 로그인하지 않은 사람은 Pick을 하나도 만들 수 없고, "일단 써보고 나중에
 * 로그인" 이라는 서비스의 입구가 막힌다.
 *
 * 그래서 로그인 전에는 고른 사진을 여기(IndexedDB)에 들고 있다가, 로그인해서
 * 목록을 계정으로 옮길 때 그때 올린다.
 *
 * localStorage가 아니라 IndexedDB인 이유: localStorage는 문자열만 담을 수 있어
 * 사진을 base64로 부풀려야 하고(약 1.37배), 한도도 5MB 남짓이라 몇 장이면
 * 목록 전체가 저장 실패한다. IndexedDB는 Blob을 그대로 담는다.
 */

const DB_NAME = 'lifetimeline';
const STORE = 'pickImages';
const VERSION = 1;

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB를 열 수 없습니다'));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      const request = run(transaction.objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB 요청 실패'));
    });
  } finally {
    db.close();
  }
}

/** 카테고리당 한 장이라 키를 categoryId로 둔다 — 교체가 곧 덮어쓰기다 */
export async function putLocalImage(categoryId: string, blob: Blob): Promise<void> {
  await withStore('readwrite', (store) => store.put(blob, categoryId));
}

export async function getLocalImage(categoryId: string): Promise<Blob | null> {
  try {
    return (
      (await withStore<Blob | undefined>('readonly', (store) => store.get(categoryId))) ?? null
    );
  } catch {
    // 시크릿 창·저장소 차단. 사진을 못 보여줄 뿐 목록은 살아 있어야 한다.
    return null;
  }
}

export async function removeLocalImage(categoryId: string): Promise<void> {
  try {
    await withStore('readwrite', (store) => store.delete(categoryId));
  } catch {
    // 무시 — 뒷정리다
  }
}

/** 계정으로 옮기고 나면 비운다. **업로드가 끝난 뒤에만** 부른다 */
export async function clearLocalImages(): Promise<void> {
  try {
    await withStore('readwrite', (store) => store.clear());
  } catch {
    // 무시
  }
}
